import { styled } from '@glitz/react';
import * as React from 'react';
import { LineArrowRight } from 'webapp/components/icons';
import Link from 'webapp/components/Link';
import { edgeMarginsDecorator, maxContentNormalWidthDecorator } from 'webapp/components/page-layout';
import {
  createMinLargeViewportDecorator,
  createMaxSmallViewportDecorator,
  createMinMediumViewportDecorator,
  createMaxMobileViewportDecorator,
} from 'webapp/viewport';

type PropType = {
  children: React.ReactNode;
  backgroundColor: string;
  heading: string;
  showMoreLink?: string;
  showMoreText?: string;
  showMoreLinkPosition?: NordicNest.Api.ShowMoreLinkPosition;
  id?: string;
  centerHeader?: boolean;
};

export default React.memo(
  styled(function BaseContentList({
    children,
    backgroundColor,
    heading,
    showMoreLink,
    showMoreText,
    showMoreLinkPosition = 'top',
    id,
    centerHeader = true,
  }: PropType) {
    const showMore = Boolean(showMoreLink && showMoreText);

    return (
      <Base id={id} css={{ backgroundColor: backgroundColor }}>
        <TopContent css={centerHeader && topContentCenterHeader}>
          <Heading css={centerHeader && centerHeaderDecorator}>{heading}</Heading>
          {showMore && showMoreLinkPosition === 'top' && (
            <ShowMore to={showMoreLink} css={centerHeader && centerShowMoreDecorator}>
              {showMoreText}
              <ArrowRight />
            </ShowMore>
          )}
        </TopContent>
        <Content>
          {children}
          {showMore && showMoreLinkPosition === 'bottom' && (
            <ShowMore to={showMoreLink} css={centerHeader && centerShowMoreDecorator}>
              {showMoreText}
              <ArrowRight />
            </ShowMore>
          )}
        </Content>
      </Base>
    );
  }),
);

const Base = styled.div({
  width: '100%',
});

const TopContent = styled.div(
  edgeMarginsDecorator,
  maxContentNormalWidthDecorator,
  createMinMediumViewportDecorator({
    gridTemplateColumns: 'minmax(max-content, 1fr) minmax(max-content, 1fr) minmax(max-content, 1fr)',
    display: 'grid',
    gridTemplateAreas: '"header . link"',
    alignItems: 'center',
    columnGap: t => t.spacing.xs,
  }),
  {
    marginBottom: t => t.sharedSpacing.xl,
  },
  createMaxSmallViewportDecorator({ padding: { x: t => t.sharedSpacing.m } }),
);

const Content = styled.div(
  edgeMarginsDecorator,
  maxContentNormalWidthDecorator,
  createMinLargeViewportDecorator({
    marginBottom: t => `${t.sharedSpacing.xl * 2}px`,
  }),
  {
    padding: { x: 0 },
    marginBottom: t => t.sharedSpacing.l,
  },
);

const topContentCenterHeader = styled(
  createMinMediumViewportDecorator({
    gridTemplateAreas: '". header link"',
  }),
  {
    gridTemplateAreas: '". header link"',
  },
);

const Heading = styled.h2({
  fontWeight: t => t.fontWeight?.bold,
  fontSize: t => t.fontSize.l,
  marginBottom: 0,
  gridArea: 'header',
});

const centerHeaderDecorator = styled({
  textAlign: 'center',
});

const ShowMore = styled(
  Link,
  createMaxMobileViewportDecorator({
    padding: { top: t => t.spacing.xs, x: t => t.spacing.xs },
  }),
  createMinMediumViewportDecorator({
    width: 'auto',
    justifyContent: 'flex-end',
  }),
  {
    width: '100%',
    alignItems: 'center',
    display: 'flex',
    fontSize: t => t.sharedFontSize.xs,
    gridArea: 'link',
  },
);

const centerShowMoreDecorator = styled({
  justifyContent: 'center',
});

const ArrowRight = styled(LineArrowRight, {
  marginLeft: t => t.sharedSpacing.s,
  fontSize: t => t.sharedFontSize.xxs,
});
